import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import  { LinkPresentationProps, StyledClickable } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { USER_ROLES } from "@store/constants";
import { sideMenuLinkActiveStyles, sideMenuLinkStyles } from "@store/theme/common/mixins";
import { TYPOGRAPHY } from "@store/theme/design";
import isTheme from "@store/utilities/isTheme";
import { AccountListModel } from "@store/Widgets/LoginButton";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    displaySignOut?: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const loginButtonWidget = getWidgetsByPageId(state,getHeader(state).id ).find((widget: WidgetProps) => {
        return widget.type === "LoginButton";
    });
    const accountMenuLinks =
    (loginButtonWidget?.fields.links as AccountListModel[]) ?? [];

    const loginButtonFields =
    (loginButtonWidget?.fields) ?? [];

    const userRoles = state.context.session?.userRoles;

    return {
        loginButtonFields,
        accountMenuLinks,
        currentLocation: getLocation(state),
        isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
        isUserAdmin: userRoles?.includes(USER_ROLES.administrator),
        isUserBuyer3: userRoles?.includes(USER_ROLES.buyer3),
        currentBillTo: getCurrentBillToState(state).value,
    }
}

const mapDispatchToProps = {
    signOut,
};

export interface AccountProps {
    onClick?: React.EventHandler<React.MouseEvent>;
}

export type SideMenuLinkPresentationProps = LinkPresentationProps & {
    isCurrent?: boolean;
};

interface AccountListStyle {
    wrapper: InjectableCss;
    categoryName: TypographyPresentationProps;
    link: LinkPresentationProps;
    signOutLink?: SideMenuLinkPresentationProps;
}

const _linkStyles = () => css<SideMenuLinkPresentationProps>`
    ${sideMenuLinkStyles("0.3em")}

    ${({ isCurrent }) =>
        isCurrent &&
        css`
            ${sideMenuLinkActiveStyles()}
        `}
`;

const accountListStyle: AccountListStyle = {
    wrapper: {
        css: css`
            width: 100%;
            font-size: ${TYPOGRAPHY.size.smaller};
            display: flex;
            flex-direction: column;
            & > :first-child > p {
                border-top: 0;
            }
        `,
    },
    categoryName: {
        forwardAs: "p",
        css: css`
            margin: 0.75em 1.3em 0 1em;
            font-weight: 700;
            padding: 0.75em 0;
            border-left: 4px solid transparent;
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
    link: {
        color: "text.main",
        css: css`
            ${_linkStyles()};
            padding-left: 1em;
            padding-right: 1em;
        `
    },
    signOutLink: {
        css: css<SideMenuLinkPresentationProps>`
            ${_linkStyles()}
            margin: 1.5em 0 1em;
			padding-left: 1em;
            ${({ theme }) => {
                if (isTheme(theme, "horizon")) {
                    return css`
                        color: ${getColor("primary.dark")};
                    `;
                }

                return css`
                    color: ${getColor("primary")};
                `;
            }}
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
}
const styles = accountListStyle;

type Props = OwnProps & AccountProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const AccountMenuLinkList = ({
    accountMenuLinks,
    currentLocation,
    signOut,
    onClick,
    isSignedIn,
    isUserAdmin,
    isUserBuyer3,
    currentBillTo,
    displaySignOut,
}: Props) => {
    const links = useGetLinks(accountMenuLinks, (o: any) => o.fields.LinkField);
    const currentPageUrl = currentLocation.pathname.toLowerCase();
    const customerCountry = currentBillTo?.country?.abbreviation;
    const isCountryCanada = customerCountry === "CA";

    return (
        <StyledWrapper {...styles.wrapper}>
           {accountMenuLinks.map((fields, index) => (
                 // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper key={index}>
                    {fields.fields.isCategoryTitle === "Category" && (
                        <Typography {...styles.categoryName}>
                            {fields.fields.categoryTitle}
                        </Typography>
                    )}
                {fields.fields.isCategoryTitle === "Link" && (
                    ((fields.fields.hideForCanadianCustomers && !isCountryCanada) || !fields.fields.hideForCanadianCustomers) && (
                        (fields.fields.showOnlyAdmin && isUserAdmin) ||
                        (fields.fields.showOnlyAdminBuyer3 && (isUserAdmin || isUserBuyer3)) ||
                        (!fields.fields.showOnlyAdmin && !fields.fields.showOnlyAdminBuyer3)
                            ? (
                                <StyledClickable 
                                    href={links[index].url}
                                    isCurrent={currentPageUrl.endsWith(links[index].url?.toLowerCase() || "")}
                                    onClick={onClick}
                                    {...styles.link}
                                >
                                    { fields.fields.linkName || links[index].title }
                                </StyledClickable>
                            )
                            : null
                    )
                )}
                </StyledWrapper>
               ))}
             {displaySignOut && isSignedIn &&  (
                <StyledClickable
                    onClick={(event: React.MouseEvent) => {
                        signOut();
                        onClick?.(event);
                    }}
                    {...styles.signOutLink}
                >
                    {translate("Sign Out")}
                </StyledClickable>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountMenuLinkList);
